import { getFullUrl } from 'hubspot-url-utils';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import useSearchParams from 'academy-common-lib/hooks/useSearchParams';
import { chirpEnumToString } from 'academy-common-lib/utils/chirp';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { EditorPage } from 'foundations-components/transitional/components/EditorPage';
import LearnByDoingDraggableContainer from '../toolRecommendations/components/LearnByDoing/DraggableContainer/LearnByDoingDraggableContainer.migrated';
import styled from 'foundations-theming/utils';
import { NavMarker } from 'react-rhumb';
import { useHomepageUrl } from '../navigation';
import { useFullShort } from '../shortContent/index.migrated';
import { Flex } from 'foundations-components/components/Flex';
import { useCallback, useEffect } from 'react';
import UsageTracker from '../../utils/UsageTracker';
import ACMissingContentError from 'academy-common-lib/components/error/ACMissingContentError';
import { Button } from 'foundations-components/transitional/components/Button';
import { InfoIcon } from 'foundations-assets/icons/components/InfoIcon';
import { LockedIcon } from 'foundations-assets/icons/components/LockedIcon';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { Popover, PopoverHeader, PopoverBody, PopoverFooter } from 'foundations-components/transitional/components/Popover';
import { H3 } from 'foundations-components/transitional/components/H3';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useIsPracticeModeEnabled } from './practiceModeHooks.migrated';
import { getPracticeModeConfig } from './practiceModeConsts.migrated';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FullscreenIframe = styled.iframe.withConfig({
  displayName: "PracticeMode__FullscreenIframe"
})(["width:100%;height:100%;border:0;overflow:scroll;position:absolute;"]);
function TopBar({
  title,
  buttons
}) {
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    backgroundColor: "fill-info-subtle",
    borderBlockColor: "border-info-default",
    borderInlineColor: "border-info-default",
    borderRadius: "100",
    borderStyle: "solid",
    borderWidth: "100",
    inlineSize: "100%",
    justifyContent: "space-between",
    minBlockSize: "60px",
    paddingBlock: "400",
    paddingInline: "500",
    children: [/*#__PURE__*/_jsx(Text, {
      typography: "heading-100",
      children: title
    }), buttons]
  });
}
function getOverrideUrl() {
  try {
    return localStorage.getItem('Academy:PracticeModeHomePage');
  } catch (error) {
    return null;
  }
}
const useIframeUrl = ({
  practicePortalId
}) => {
  const {
    homepage,
    homepageLoading
  } = useHomepageUrl();
  if (homepageLoading || !homepage) {
    return null;
  }
  const overrideUrl = getOverrideUrl();
  if (overrideUrl) {
    return overrideUrl;
  }

  // useHomepageUrl returns the url with portalId in the path
  const withPortalId = homepage ? homepage.replace(/\/\d+(?=\/|$)/, `/${practicePortalId}`) : null;
  return `${getFullUrl('app')}${withPortalId}`;
};
function UpgradeButton({
  toolPath,
  originPortalId,
  hasScope,
  upgradePoint,
  shortId,
  visibility
}) {
  if (hasScope) {
    return /*#__PURE__*/_jsx(Button, {
      external: true,
      href: `${getFullUrl('app')}/${toolPath}/${originPortalId}`,
      onClick: () => {
        UsageTracker.track('contentInteraction', {
          action: 'practice-mode-software-cta-clicked',
          shortId: String(shortId),
          visibility
        });
      },
      size: "extra-small",
      use: "tertiary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.practiceMode.banner.softwareCta"
      })
    });
  }
  return /*#__PURE__*/_jsxs(Button, {
    external: true,
    href: `${getFullUrl('app')}/pricing/${originPortalId}/upgrade?upgradeSource=${upgradePoint}`,
    onClick: () => {
      UsageTracker.track('contentInteraction', {
        action: 'practice-mode-upgrade-cta-clicked',
        shortId: String(shortId),
        visibility
      });
    },
    size: "extra-small",
    use: "tertiary",
    children: [/*#__PURE__*/_jsx(LockedIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.practiceMode.banner.workspaceCta"
    })]
  });
}
const useHelpPopoverStore = create()(persist(set => ({
  isOpen: false,
  setIsOpen: isOpen => set({
    isOpen
  })
}), {
  name: 'academy:practice-mode-help-popover'
}));
function HelpPopover() {
  const {
    isOpen,
    setIsOpen
  } = useHelpPopoverStore();
  return /*#__PURE__*/_jsx(Popover, {
    content: /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(PopoverHeader, {
        children: /*#__PURE__*/_jsx(H3, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.practiceMode.popover.title"
          })
        })
      }), /*#__PURE__*/_jsxs(PopoverBody, {
        paddingBlock: "300",
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "body-125",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.practiceMode.popover.question1"
          })
        }), /*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.practiceMode.popover.answer1"
          })
        }), /*#__PURE__*/_jsx(Text, {
          typography: "body-125",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.practiceMode.popover.question2"
          })
        }), /*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.practiceMode.popover.answer2"
          })
        }), /*#__PURE__*/_jsx(Text, {
          typography: "body-125",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.practiceMode.popover.question3"
          })
        }), /*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.practiceMode.popover.answer3"
          })
        })]
      }), /*#__PURE__*/_jsx(PopoverFooter, {
        padding: "500",
        children: /*#__PURE__*/_jsx(Button, {
          onClick: () => setIsOpen(false),
          use: "tertiary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.practiceMode.popover.close"
          })
        })
      })]
    }),
    maxInlineSize: "350px",
    onOpenChange: e => setIsOpen(e.currentTarget.value),
    open: isOpen,
    placement: "bottom left",
    children: /*#__PURE__*/_jsx(IconButton, {
      onClick: () => setIsOpen(!isOpen),
      size: "extra-small",
      tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.practiceMode.popover.tooltip"
      }),
      use: "tertiary",
      children: /*#__PURE__*/_jsx(InfoIcon, {})
    })
  });
}
export const PracticeMode = () => {
  const [searchParams] = useSearchParams();
  const {
    otg: shortId,
    practicePortalId,
    toolPath,
    originPortalId,
    hasScope,
    upgradePoint
  } = searchParams;
  const config = getPracticeModeConfig(Number(shortId));
  const iframeUrl = useIframeUrl({
    practicePortalId
  });
  const skipFetch = !shortId;
  const {
    isPracticeModeEnabled
  } = useIsPracticeModeEnabled(Number(shortId));
  const {
    short,
    shortLoading
  } = useFullShort(Number(shortId), skipFetch);
  const {
    contentHublet: hublet
  } = short || {};
  const visibility = chirpEnumToString(short === null || short === void 0 ? void 0 : short.visibility);
  useEffect(() => {
    const hideZorse = document.createElement('style');
    hideZorse.innerHTML = `#isc-zorse-widget, #chat-widget, #academy-toolbar {
        display:none !important; }`;
    document.getElementsByTagName('head')[0].appendChild(hideZorse);
    return function cleanup() {
      hideZorse.remove();
    };
  }, []);
  const handleIframeLoad = useCallback(event => {
    try {
      var _iframeDoc$getElement;
      const iframeDoc = event.currentTarget.contentDocument;
      if (!iframeDoc) return;
      (_iframeDoc$getElement = iframeDoc.getElementById('academy-toolbar')) === null || _iframeDoc$getElement === void 0 || _iframeDoc$getElement.remove();
      const style = iframeDoc.createElement('style');
      style.textContent = '#academy-toolbar { display: none !important; }';
      iframeDoc.head.appendChild(style);
    } catch (e) {
      // Cross-origin iframe — cannot touch its document
    }
  }, []);
  if (!shortId || !practicePortalId || !iframeUrl) {
    return null;
  }
  const missingShort = !shortLoading && !short;
  if (missingShort || !isPracticeModeEnabled) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "PRACTICE_MODE_ERROR",
      children: /*#__PURE__*/_jsx(ACMissingContentError, {})
    });
  }
  return /*#__PURE__*/_jsx(EditorPage, {
    hasScrolling: true,
    headerComponent: /*#__PURE__*/_jsx(TopBar, {
      buttons: /*#__PURE__*/_jsxs(ButtonWrapper, {
        children: [/*#__PURE__*/_jsx(Button, {
          href: `${getFullUrl('app')}/academy/${originPortalId}/shortvideo/${shortId}`,
          onClick: () => {
            UsageTracker.track('contentInteraction', {
              action: 'practice-mode-back-to-academy-clicked',
              shortId: String(shortId),
              visibility
            });
          },
          size: "extra-small",
          use: "primary-white",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.practiceMode.banner.academyCta"
          })
        }), /*#__PURE__*/_jsx(UpgradeButton, {
          hasScope: hasScope === 'true',
          originPortalId: originPortalId,
          shortId: Number(shortId),
          toolPath: toolPath || (config === null || config === void 0 ? void 0 : config.toolPath) || '',
          upgradePoint: upgradePoint || (config === null || config === void 0 ? void 0 : config.upgradePoint) || '',
          visibility: visibility
        }), /*#__PURE__*/_jsx(HelpPopover, {})]
      }),
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.practiceMode.banner.description"
      })
    }),
    inset: "0",
    mainSectionFlush: true,
    position: "absolute",
    use: "dark",
    zIndex: 5,
    children: /*#__PURE__*/_jsx(DndProvider, {
      backend: HTML5Backend,
      children: /*#__PURE__*/_jsx(LearnByDoingDraggableContainer, {
        creatorHublet: hublet,
        creatorPortalId: Number(practicePortalId),
        iFrameId: "practice-mode-iframe",
        otgId: Number(shortId),
        children: /*#__PURE__*/_jsx(NavMarker, {
          name: "PRACTICE_MODE_LOADED",
          children: /*#__PURE__*/_jsx(FullscreenIframe, {
            "data-test-id": "practice-mode-iframe",
            id: "practice-mode-iframe",
            onLoad: handleIframeLoad,
            src: iframeUrl
          })
        })
      })
    })
  });
};